/**
 * Hidden-form POST to Shopify's cart-add endpoint.
 *
 * Kept in its own module (no Nuxt/#app imports) so it can be exercised in
 * a jsdom unit test without spinning up the Nuxt runtime.
 */

export interface CartAddPayloadItem {
	id: string
	quantity: number
	properties?: Record<string, string | number>
}

export interface CartAddPayload {
	action: string
	items: CartAddPayloadItem[]
}

export interface CommercialFields {
	isCommercialProject: boolean
	salesperson: string | null
	projectName: string | null
}

/**
 * Builds a hidden HTML form with Shopify's `items[i][...]` naming and submits
 * it top-level. Form encoding (application/x-www-form-urlencoded) avoids the
 * paste-redirect chain that re-encodes GET URLs — Shopify decodes values
 * exactly once and stores them clean on the cart line.
 */
export function submitCartAddViaForm(payload: CartAddPayload, commercial?: CommercialFields) {
	const form = document.createElement('form')
	form.method = 'POST'
	form.action = payload.action
	form.style.display = 'none'

	const appendInput = (name: string, value: string) => {
		const input = document.createElement('input')
		input.type = 'hidden'
		input.name = name
		input.value = value
		form.appendChild(input)
	}

	payload.items.forEach((item, i) => {
		appendInput(`items[${i}][id]`, item.id)
		appendInput(`items[${i}][quantity]`, String(item.quantity))
		if (item.properties) {
			Object.entries(item.properties).forEach(([k, v]) => {
				appendInput(`items[${i}][properties][${k}]`, String(v))
			})
		}
	})

	if (commercial?.isCommercialProject) {
		if (commercial.salesperson) appendInput('salesperson', commercial.salesperson)
		appendInput('commercial_project', 'true')
		if (commercial.projectName) appendInput('project_name', commercial.projectName)
	}

	document.body.appendChild(form)
	form.submit()
}
